/**
 * The eval-probe flag, on its own so a page without the Phaser world (the share page, the
 * parent's oversight view) can read it without pulling the engine in.
 *
 * `?wbm_probe=1` turns it on, sticky for the tab (the app rewrites its own URL, so a
 * query flag has to outlive the first navigation, like `?wbm_caps=`); `?wbm_probe=0`
 * clears it. Nothing gated on it runs for a kid.
 */

const FLAG_KEY = "wbm.eval_probe";

export function evalProbeEnabled(): boolean {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("wbm_probe");
    if (fromUrl === "0") window.sessionStorage.removeItem(FLAG_KEY);
    else if (fromUrl !== null) window.sessionStorage.setItem(FLAG_KEY, "1");
    return window.sessionStorage.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

const MUTE_KEY = "wbm.eval_mute";

/**
 * `?wbm_mute=1` (sticky like the probe, `?wbm_mute=0` clears it), honoured only with the probe
 * on: a walk's WebKit has no `--mute-audio`, and a harness run against a deployed build played
 * the Warnock bed through this Mac's speakers (2026-10-27). The sound host and the World zero
 * their master gain and nothing else, so the play log, onsets and `hit_at` still read true.
 * Never keyed on `navigator.webdriver`: a deployed walk takes a kid's path.
 */
export function evalMuteEnabled(): boolean {
  if (!evalProbeEnabled()) return false;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("wbm_mute");
    if (fromUrl === "0") window.sessionStorage.removeItem(MUTE_KEY);
    else if (fromUrl !== null) window.sessionStorage.setItem(MUTE_KEY, "1");
    return window.sessionStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Add a named reader to `window.__wbmProbe`, keeping whatever the world already put there. */
export function installProbe(name: string, reader: () => unknown): void {
  if (!evalProbeEnabled()) return;
  const w = window as unknown as { __wbmProbe?: Record<string, unknown> };
  w.__wbmProbe = { ...(w.__wbmProbe ?? {}), [name]: reader };
}
